import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, catchError, forkJoin, Observable, of, switchMap, tap } from 'rxjs';
import { AppInsightsService } from './app-insights.service';
import { CommonConstants } from 'src/app/shared/constants/common-constants';
import { CommonFunctions } from 'src/app/shared/utilities/common-functions';
import { ConfigurationFunctionService, CompanyInfo } from './configurationapi/configurationfunctionapi.services';
import { HierarchicalPermissionDto } from './iamapi/iamapi.services';
import { HierarchicalPermissionsService } from './hierarchical-permissions.service';
import { IamWrapperService } from './iam-wrapper.service';
import { StorageConstants } from 'src/app/shared/constants/storage-constants';
import { ScreenRole, Screens, ScreenUser, UserRoleCompany, CompanyModel} from '../models/iam-dtos';

@Injectable({
  providedIn: 'root'
})
export class ShellService {

  private readonly fetchNotificationsSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private readonly logoutObservableSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private readonly sideNavCollapseSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private readonly companyChangeSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private readonly isShellDataLoadedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private readonly noAccessSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public fetchNotifications$ = this.fetchNotificationsSubject.asObservable();
  public logoutObservable$ = this.logoutObservableSubject.asObservable();
  public sideNavCollapse$ = this.sideNavCollapseSubject.asObservable();
  public companyChange$ = this.companyChangeSubject.asObservable();
  public isShellDataLoaded$ = this.isShellDataLoadedSubject.asObservable();
  public noAccess$ = this.noAccessSubject.asObservable();

  navigationData = {
    companyList: [],
    firstmenuUrl: '',
    parentScreens: []
  };

  navigationStartUrl = '';
  screenList: Screens[] = [];
  parentScreens: any[] = [];
  accessRightsList = '';
  firstMenuItem = '';
  companyAndScreenPermissionsData: {
    screenPermissions: ScreenUser;
    companyData: CompanyModel[];
  } = { screenPermissions: null, companyData: [] };

  constructor(
    private readonly applicationInsightsService: AppInsightsService,
    private readonly commonFunctions: CommonFunctions,
    private readonly configApiService: ConfigurationFunctionService,
    private readonly hierarchicalPermissionsService: HierarchicalPermissionsService,
    private readonly iamWrapperService: IamWrapperService,
    private readonly router: Router,
    private readonly toastr: ToastrService
  ) { }

  setFetchNotifications(value: boolean) {
    this.fetchNotificationsSubject.next(value);
  }

  setLogoutObservable(value: boolean) {
    this.logoutObservableSubject.next(value);
  }

  setSideNavCollapse(value: boolean) {
    this.sideNavCollapseSubject.next(value);
  }

  setCompanyChange(value: boolean) {
    this.companyChangeSubject.next(value);
  }

  setShellDataLoaded(value: boolean) {
    this.isShellDataLoadedSubject.next(value);
  }

  setNoAccess(value: boolean) {
    this.noAccessSubject.next(value);
  }

  loadDataOnCompanyChange$(): Observable<{
    permissions: HierarchicalPermissionDto;
    companyInfo: CompanyInfo;
  }> {
    return this.iamWrapperService.getUserAccessedCompaniesAndScreenPermissions().pipe(
      tap((data) => this.setCompanyDataInSessionStorage(data)),
      switchMap(() => this.loadData$()),
      catchError((error) => {
        this.setNoAccess(true);
        this.commonFunctions.hideLoading();
        this.commonFunctions.handleErrorResponse(error, this.toastr, CommonConstants.UnableToLoadData);
        return of({
          screenPermissions: null,
          permissions: null,
          companyInfo: null,
        });
      })
    );
  }

  loadData$(): Observable<{
    screenPermissions: ScreenUser;
    permissions: HierarchicalPermissionDto;
    companyInfo: CompanyInfo;
  }> {
    return forkJoin({
      screenPermissions: of(this.companyAndScreenPermissionsData.screenPermissions),
      permissions: this.iamWrapperService.getUserAssignedHierarchicalPermissions(),
      companyInfo: this.configApiService.getCompanyInfo(),
      audit: this.iamWrapperService.addLatestAccessedCompany(
        this.getUserRoleCompanyDto(this.companyAndScreenPermissionsData.screenPermissions))
    }).pipe(
      tap(({ screenPermissions, permissions, companyInfo }) =>
        this.setCompanyConfigsFeaturesAndCreateScreenData({ screenPermissions, permissions, companyInfo })),
      catchError((error) => {
        this.setNoAccess(true);
        this.commonFunctions.hideLoading();
        this.commonFunctions.handleErrorResponse(error, this.toastr, CommonConstants.UnableToLoadData);
        return of({
          screenPermissions: null,
          permissions: null,
          companyInfo: null,
        });
      })
    );
  }

  setCompanyAgnosticConfigsInSessionStorage(companyAgnosticConfigs: CompanyInfo) {
    if (companyAgnosticConfigs) {
      sessionStorage.setItem(CommonConstants.CompanyAgnosticFeaturesStorageKey, btoa(JSON.stringify(companyAgnosticConfigs.features)));
    }
  }

  setCompanyDataInSessionStorage(data: {screenPermissions: ScreenUser; companyData: CompanyModel[]}) {
    this.companyAndScreenPermissionsData = data;
    if (data.companyData?.length > 0) {
      this.navigationData.companyList = data.companyData;
      const existingCompanyCode = sessionStorage.getItem(StorageConstants.CompanyCode);
      if (!existingCompanyCode) {
        const [company] = data.companyData;
        if (company) {
          sessionStorage.setItem(StorageConstants.CompanyId, JSON.stringify(company.companyId));
          sessionStorage.setItem(StorageConstants.CompanyCode, company.companyName);
        }
      }
    }
  }

  private setCompanyConfigsFeaturesAndCreateScreenData(data: {
    screenPermissions: ScreenUser;
    permissions: HierarchicalPermissionDto;
    companyInfo: CompanyInfo;
  }) {
    if (data.permissions) {
      this.hierarchicalPermissionsService.userPermissions = data.permissions;
    }
    if (data.companyInfo) {
      sessionStorage.setItem(CommonConstants.CompanyFeaturesStorageKey, btoa(JSON.stringify(data.companyInfo.features)));
      sessionStorage.setItem(CommonConstants.CompanyConfigStorageKey, btoa(JSON.stringify(data.companyInfo.configuration)));
    }
    if(data.screenPermissions?.roles?.length > 0) {
      this.screenList = [];
      this.parentScreens = [];
      this.accessRightsList = '';
      this.createScreenPermissions(data.screenPermissions.roles);
    }
    if(data.screenPermissions) {
      this.setUserDataInAIContext(data.screenPermissions);
      if(data.screenPermissions.objectId) {
        sessionStorage.setItem(StorageConstants.ObjectId, data.screenPermissions.objectId);
      }
    }
  }

  private getUserRoleCompanyDto(data: ScreenUser): UserRoleCompany {
    const userRoleCompany = new UserRoleCompany();
    userRoleCompany.userId = JSON.stringify(data.objectId);
    userRoleCompany.companyId = +sessionStorage.getItem(StorageConstants.CompanyId);
    userRoleCompany.roleId = this.extractRoleIds(data);
    return userRoleCompany;
  }

  private extractRoleIds(data: ScreenUser): number[] {
    let roleIds = [];
    if(data?.roles?.length > 0) {
      roleIds = data.roles.map(role => role.roleId);
    }
    return roleIds;
  }

  private createScreenPermissions(roles: ScreenRole[]) {
    roles.forEach(element => {
      element.screens.forEach(screen => {
        this.screenList.push(screen);
      });
    });
    const codesToDisable = this.iamWrapperService.getListOfCodesToDisable();
    this.screenList.forEach(screen => {
      if (codesToDisable.includes(screen.screenCode)) {
        screen.display = false;
      }
    });
    if (roles.some(x => x.canImpersonate)) {
      sessionStorage.setItem(StorageConstants.CanImpersonate, btoa(JSON.stringify(true)));
    }
    sessionStorage.setItem(StorageConstants.ScreenPermissions, btoa(JSON.stringify(this.screenList)));
    this.parentScreens = this.screenList.filter(x => x.parentId === null && x.display);
    this.generateList();
    sessionStorage.setItem(StorageConstants.UserAccess, btoa(this.accessRightsList));
    this.navigationData.parentScreens = this.parentScreens;
  }

  private generateList() {
    const currentUrl = this.router.url;
    this.parentScreens.forEach(element => {
      const elchilderns = this.getChilds(element.screenId);
      const parentHasCurrentRoute = elchilderns.filter(x => x.path && x.path !== '' && x.display === true
        && currentUrl.indexOf(x.path) > -1);
      if (parentHasCurrentRoute && parentHasCurrentRoute.length > 0) {
        element['expanded'] = true;
      } else {
        element['expanded'] = false;
      }
      element['childerns'] = elchilderns;
      if (elchilderns?.length) {
        this.accessRightsList += elchilderns.map(x => x.path).join(',');
      }

      this.processChildMenu(element, currentUrl);
      this.accessRightsList += ',';
    });
    this.accessRightsList = this.accessRightsList.split(',').filter(x => x).join(',');
    const newMenuList = this.parentScreens;
    newMenuList.sort((a, b) => a.displayOrder < b.displayOrder ? -1 : 1);
    const sortedParent = newMenuList;
    sortedParent.forEach(element => {
      element.childerns = element.childerns.sort((a, b) => a.displayOrder < b.displayOrder ? -1 : 1);
    });
    const firstParentMenu = this.parentScreens[0].childerns.find(x => x.display === true);
    this.firstMenuItem = (firstParentMenu === undefined || firstParentMenu === null) ? '' : firstParentMenu.path;
    this.navigationData.firstmenuUrl = this.firstMenuItem;
  }

  private setUserDataInAIContext(data: ScreenUser) {
    const roleNames = [];
    data.roles.forEach(x => roleNames.push(x.roleName));
    const userData = { ...data, roleNames: roleNames.join(', ') };
    userData.roles = undefined;
    this.applicationInsightsService.userInfo.UserRole = userData.roleNames;
  }

  private getChilds(_parentId: number) {
    return this.screenList.filter(x => x.parentId === _parentId && x.isAuthorized);
  }

  private processChildMenu(element: any, currentUrl: string) {
    if (element?.childerns?.length > 0) {
      this.accessRightsList += ',';
      element.childerns.forEach(childrens => {
        this.accessRightsList += ',';
        const parentchildrens = this.getChilds(childrens.screenId);
        childrens['childerns'] = parentchildrens;
        const childHasCurrentRoute = parentchildrens.filter(x => x.path && x.path !== '' && x.display === true
          && currentUrl.indexOf(x.path) > -1);
        if (childHasCurrentRoute && childHasCurrentRoute.length > 0) {
          element['expanded'] = true;
          childrens['expanded'] = true;
        } else {
          if (element['expanded']) {
            element['expanded'] = false;
          }
          childrens['expanded'] = false;
        }
        this.accessRightsList += childrens['childerns'].map(x => x.path).join(',');
        this.processChildMenu(childrens, currentUrl);
      });
    }
  }
}
